import { z } from "zod";
import { optionalPhoneBRSchema } from "../common/phone";
import { brazilianStateSchema } from "../user/cro";
import { cepSchema } from "../common/cep";
import { quantidadeDoServicoSchema } from "./valor-do-pedido";

/** BusinessStatus — estado de negócio compartilhado entre clínica e laboratório, deliberadamente
 *  enxuto. Detalhe de bancada vive em ProductionStage (por organização), nunca aqui. */
export const prosthesisStatusSchema = z.enum([
  "AGUARDANDO_CONFIRMACAO",
  "RECUSADO",
  "ACEITO",
  "EM_PRODUCAO",
  "CONCLUIDO",
  "CANCELADO",
]);
export type ProsthesisStatus = z.infer<typeof prosthesisStatusSchema>;

export const prosthesisLabStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);
export type ProsthesisLabStatus = z.infer<typeof prosthesisLabStatusSchema>;

const optionalEmpty = (max = 200) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .or(z.literal("").transform(() => undefined));

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .optional()
  .or(z.literal("").transform(() => undefined));

// ─── Laboratórios (lista própria do módulo) ──────────

const prosthesisLabBaseSchema = z.object({
  name: z.string().trim().min(2, "Nome muito curto").max(160),
  tradeName: optionalEmpty(160),
  email: optionalEmail,
  phone: optionalPhoneBRSchema,
  document: optionalEmpty(20),
  city: optionalEmpty(100),
  state: brazilianStateSchema.optional(),
  specialty: optionalEmpty(120),
  notes: optionalEmpty(2000),
  status: prosthesisLabStatusSchema.default("ACTIVE"),
});

export const prosthesisLabCreateSchema = prosthesisLabBaseSchema;
export const prosthesisLabUpdateSchema = prosthesisLabBaseSchema.partial().extend({
  id: z.string().min(1),
  // Sem o default: no zod 4 o default vale mesmo com o campo ausente, e editar sem mandar o campo o reescreveria.
  status: prosthesisLabStatusSchema.optional(),
});
export type ProsthesisLabCreateInput = z.infer<typeof prosthesisLabCreateSchema>;
export type ProsthesisLabUpdateInput = z.infer<typeof prosthesisLabUpdateSchema>;

// ─── Convite de conexão real clínica ↔ laboratório ───

/** Compartilhado com PartnerConnectInvite (mesmo ciclo de vida nos dois sentidos). */
export const connectInviteStatusSchema = z.enum([
  "PENDING",
  "ACCEPTED",
  "REVOKED",
  "EXPIRED",
]);
export type ConnectInviteStatus = z.infer<typeof connectInviteStatusSchema>;

/** Ou aponta pra um ProsthesisLab solto já cadastrado (ponte legada), ou informa um nome livre —
 *  nunca os dois, nunca nenhum. No fluxo novo (sem prosthesisLabId), o registro só é criado no aceite. */
export const prosthesisLabConnectInviteCreateSchema = z
  .object({
    prosthesisLabId: z.string().min(1).optional(),
    labDisplayName: z.string().trim().min(2, "Nome muito curto").max(160).optional(),
    /** Quantos dias antes de expirar. Default 14. */
    expiresInDays: z.number().int().min(1).max(90).default(14),
  })
  .refine((data) => !!data.prosthesisLabId !== !!data.labDisplayName, {
    message: "Informe o laboratório existente ou um nome pro convite, nunca os dois.",
  });
export type ProsthesisLabConnectInviteCreateInput = z.infer<
  typeof prosthesisLabConnectInviteCreateSchema
>;

export const prosthesisLabConnectInviteAcceptSchema = z.object({
  token: z.string().min(1),
});
export type ProsthesisLabConnectInviteAcceptInput = z.infer<
  typeof prosthesisLabConnectInviteAcceptSchema
>;

/** Busca organizações reais tipo DENTAL_LAB (públicas na busca) pra "Novo laboratório". */
export const prosthesisLabSearchSchema = z.object({
  search: z.string().trim().min(1).max(160),
});
export type ProsthesisLabSearchInput = z.infer<typeof prosthesisLabSearchSchema>;

/** Conecta direto a um laboratório real já encontrado na busca — sem convite/token. */
export const prosthesisLabConnectExistingSchema = z.object({
  organizationId: z.string().min(1),
  /** Manda o mesmo pedido por todas as clínicas de que o usuário é dono, não só pela ativa —
   *  o laboratório recebe uma solicitação por clínica, e aceita uma a uma como sempre. */
  todasAsMinhasClinicas: z.boolean().optional(),
});
export type ProsthesisLabConnectExistingInput = z.infer<
  typeof prosthesisLabConnectExistingSchema
>;

// ─── Pedidos (podem ter um ou mais serviços) ─────────

export const prosthesisServiceInputSchema = z.object({
  serviceCategoryId: z.string().min(1, "Escolha a categoria do serviço"),
  materialOptionId: z.string().min(1).nullish(),
  materialLineId: z.string().min(1).nullish(),
  shadeOptionId: z.string().min(1).nullish(),
  translucencyOptionId: z.string().min(1).nullish(),
  blockSizeId: z.string().min(1).nullish(),
  manufacturingProcessId: z.string().min(1).nullish(),
  characterizationOptionId: z.string().min(1).nullish(),
  finishingOptionId: z.string().min(1).nullish(),
  teeth: optionalEmpty(120),
  /** Ausente = o padrão (um por dente; arcada = 1), calculado no servidor. */
  quantidade: quantidadeDoServicoSchema.optional(),
  // priceCents não vem do cliente — sempre calculado e congelado server-side no momento da
  // criação (resolveClientPrice, ver packages/api/src/routers/prosthesis.ts `create`).
  notes: optionalEmpty(1000),
  attachmentFileId: z.string().min(1).nullish(),
});
export type ProsthesisServiceInput = z.infer<typeof prosthesisServiceInputSchema>;

/** Endereço de envio escolhido no pedido. Sem ele, o servidor usa o endereço da clínica. */
export const entregaDoPedidoSchema = z.object({
  destinatario: z.string().trim().min(2, "Informe o destinatário").max(160),
  telefone: z.string().trim().max(20).nullish(),
  cep: cepSchema,
  endereco: z.string().trim().min(2, "Logradouro obrigatório").max(200),
  numero: z.string().trim().min(1, "Número obrigatório").max(10),
  complemento: z.string().trim().max(100).nullish(),
  bairro: z.string().trim().min(2, "Bairro obrigatório").max(100),
  cidade: z.string().trim().min(2, "Cidade obrigatória").max(100),
  uf: brazilianStateSchema,
});
export type EntregaDoPedido = z.infer<typeof entregaDoPedidoSchema>;

const prosthesisOrderBaseSchema = z.object({
  patientId: z.string().min(1).nullish(),
  patientName: z.string().trim().min(2, "Informe o paciente").max(160),
  /** Só usado quando patientId não vem preenchido (walk-in) — cadastra o paciente de verdade
   *  no lugar de deixar só o texto solto. Não é persistido no pedido em si. */
  patientPhone: optionalPhoneBRSchema,
  labId: z.string().min(1).nullish(),
  /** Só quem pede sem ser dentista (recepção etc.) escolhe; pra dentista o servidor usa ele mesmo. */
  dentistaId: z.string().min(1).nullish(),
  description: optionalEmpty(2000),
  deadline: z.coerce.date().nullish(),
  /** Canais por onde esse pedido também foi combinado fora da plataforma — o pedido às vezes nem
   *  chega a ser enviado pelo SaaS de fato (ver ProsthesisOrder.sentChannels no schema). */
  sentChannels: z.array(z.string().trim().max(60)).max(30).default([]),
  entrega: entregaDoPedidoSchema.nullish(),
});

export const prosthesisOrderCreateSchema = prosthesisOrderBaseSchema.extend({
  services: z.array(prosthesisServiceInputSchema).min(1, "Adicione ao menos um serviço"),
  /** Pedido feito a partir de um item do plano de tratamento — o item fica ligado ao pedido. */
  itemDoPlanoId: z.string().min(1).optional(),
});
export type ProsthesisOrderCreateInput = z.infer<typeof prosthesisOrderCreateSchema>;

/**
 * Edição do pedido pela clínica, só enquanto o laboratório não confirmou.
 *
 * Sem `labId` de propósito: trocar o laboratório de um pedido já enviado deixaria o primeiro com a
 * notificação de "pedido novo" apontando pro nada. Quem errou o laboratório apaga e refaz.
 */
export const prosthesisOrderUpdateSchema = prosthesisOrderBaseSchema.omit({ labId: true }).extend({
  orderId: z.string().min(1),
  services: z.array(prosthesisServiceInputSchema).min(1, "Adicione ao menos um serviço"),
});
export type ProsthesisOrderUpdateInput = z.infer<typeof prosthesisOrderUpdateSchema>;

export const prosthesisSetSentChannelsSchema = z.object({
  orderId: z.string().min(1),
  sentChannels: z.array(z.string().trim().max(60)).max(30),
});
export type ProsthesisSetSentChannelsInput = z.infer<typeof prosthesisSetSentChannelsSchema>;

export const prosthesisServiceSetStatusSchema = z.object({
  serviceId: z.string().min(1),
  status: prosthesisStatusSchema,
});

/** Solicitação de retrabalho pelo dentista — sempre move o serviço pra "Repetir Trabalho";
 *  os arquivos anexados vão pelo módulo de Arquivos genérico (attachToEntity), não por aqui. */
export const prosthesisReworkRequestCreateSchema = z.object({
  serviceId: z.string().min(1),
  notes: z.string().trim().min(3, "Descreva o que precisa ser ajustado").max(2000),
});
export type ProsthesisReworkRequestCreateInput = z.infer<typeof prosthesisReworkRequestCreateSchema>;

export const prosthesisReworkRequestListInputSchema = z.object({
  serviceId: z.string().min(1),
});

/** De quem foi a causa do retrabalho — ver `ReworkCausa` no schema do banco. */
export const reworkCausaSchema = z.enum(["INTERNO", "CLINICA", "INDEFINIDO"]);
export type ReworkCausa = z.infer<typeof reworkCausaSchema>;

export const REWORK_CAUSA_LABEL: Record<ReworkCausa, string> = {
  INTERNO: "Erro do laboratório",
  CLINICA: "Erro da clínica",
  INDEFINIDO: "Não definido",
};

/**
 * Percentual SUGERIDO por causa — ponto de partida, nunca imposição.
 *
 * Erro interno não se cobra; erro da clínica cobra metade, que é a prática trazida pelo laboratório
 * no beta. Causa indefinida não sugere nada de propósito: é justamente o caso que exige alguém
 * decidir, e um número pronto ali viraria decisão tomada por omissão.
 */
export const REWORK_PERCENTUAL_SUGERIDO: Record<ReworkCausa, number | null> = {
  INTERNO: 0,
  CLINICA: 50,
  INDEFINIDO: null,
};

/** Decisão de cobrança de um retrabalho. `valorCents` é congelado por quem decide (ver migration). */
export const prosthesisReworkDecisionSchema = z.object({
  reworkId: z.string().min(1),
  causa: reworkCausaSchema,
  cobrar: z.boolean(),
  valorCents: z.number().int().min(0).max(100_000_000).nullable(),
});
export type ProsthesisReworkDecisionInput = z.infer<typeof prosthesisReworkDecisionSchema>;

export const prosthesisOrderListFiltersSchema = z.object({
  search: z.string().trim().max(200).optional(),
  status: prosthesisStatusSchema.optional(),
  labId: z.string().min(1).optional(),
  /** Histórico de pedidos de um paciente específico (issue #13) — ficha do paciente. */
  patientId: z.string().min(1).optional(),
  /** Pedidos de um dentista responsável; `null` = pedidos sem dentista registrado. */
  dentistaId: z.string().min(1).nullable().optional(),
  take: z.number().int().min(1).max(500).default(200),
  skip: z.number().int().min(0).default(0),
});
export type ProsthesisOrderListFilters = z.infer<
  typeof prosthesisOrderListFiltersSchema
>;

// ─── Alteração de serviço feita pelo laboratório ────────────────────────────

/** O laboratório altera o serviço direto, sem aceite da clínica (decisão do beta). Adicionar ou
 *  remover serviço fica de fora de propósito — isso é pedido novo, não alteração. */
export const prosthesisServiceUpdateSchema = z.object({
  serviceId: z.string().min(1),
  serviceCategoryId: z.string().min(1),
  materialOptionId: z.string().min(1).nullable().optional(),
  shadeOptionId: z.string().min(1).nullable().optional(),
  teeth: z.string().trim().max(120).nullable().optional(),
  quantidade: quantidadeDoServicoSchema.optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type ProsthesisServiceUpdateInput = z.infer<typeof prosthesisServiceUpdateSchema>;
